/**
 * Analytics event tracking via PostHog.
 * PostHog is loaded via script tag in index.html.
 * This module provides typed event helpers for key funnel steps.
 */

// PostHog global — loaded asynchronously from index.html
declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
      identify: (distinctId: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
    };
  }
}

function track(event: string, properties?: Record<string, unknown>) {
  window.posthog?.capture(event, properties);
}

// --- Auth events ---

export function trackSignup(method: 'google' | 'email') {
  track('user_signed_up', { method });
}

export function trackLogin(method: 'google' | 'email') {
  track('user_logged_in', { method });
}

export function trackLogout() {
  track('user_logged_out');
  window.posthog?.reset();
}

// --- Core funnel events ---

export function trackPhotoUploaded(context: string) {
  track('photo_uploaded', { context });
}

export function trackAnalysisStarted(context: string, photoId: string) {
  track('analysis_started', { context, photo_id: photoId });
}

export function trackResultsViewed(score: number, modelCount: number, context: string) {
  track('results_viewed', { score, model_count: modelCount, context });
}

export function trackRetakeComparison(oldScore: number, newScore: number, delta: number) {
  track('retake_comparison_viewed', { old_score: oldScore, new_score: newScore, delta });
}

// --- Engagement events ---

export function trackCompareStarted(photoCount: number, context: string) {
  track('compare_started', { photo_count: photoCount, context });
}

export function trackShareClicked(type: 'score' | 'quiz') {
  track('share_clicked', { type });
}

export function trackOutcomeLogged(metric: string, value: number, context: string) {
  track('outcome_logged', { metric, value, context });
}

// --- Monetization events ---

export function trackPurchasePageViewed() {
  track('purchase_page_viewed');
}

export function trackPurchaseClicked(tier: string, credits: number, priceCents: number) {
  track('purchase_clicked', { tier, credits, price_cents: priceCents });
}

export function trackCreditsExhausted() {
  track('credits_exhausted');
}

// --- Acquisition events (doc 05) ---

export function trackLandingCtaClicked(
  screen: 'landing' | 'vertical_landing',
  vertical: 'dating' | 'linkedin' | 'unknown',
  ctaId: string
) {
  track('landing_cta_clicked', { screen, vertical, cta_id: ctaId });
}

export function trackVerticalLandingClicked(vertical: 'dating' | 'linkedin') {
  track('vertical_landing_clicked', { vertical });
}

export function trackSampleResultInteracted(screen: string) {
  track('sample_result_interacted', { screen });
}

export function trackAuthCompleted(method: 'google' | 'email', sourceScreen: string) {
  track('auth_completed', { method, source_screen: sourceScreen });
}

// --- UI events ---

export function trackAuthModalOpened(mode: 'signup' | 'signin', sourceScreen?: string) {
  track('auth_modal_opened', { mode, source_screen: sourceScreen });
}

export function trackOnboardingCompleted(vertical: string) {
  track('onboarding_completed', { vertical });
}

export function trackCoachingTabClicked(tab: 'strengths' | 'improvements') {
  track('coaching_tab_clicked', { tab });
}

export function trackErrorOccurred(type: string, message: string) {
  track('error_occurred', { error_type: type, error_message: message });
}

// --- Identity ---

export function identifyUser(userId: string, email: string) {
  window.posthog?.identify(userId, { email });
}
